import { useCallback, useState } from 'react';

import { getParsedFormData } from '@/domain/job-application/helpers';
import {
  ApplyFormSubmitError,
  useApplyFormSubmitErrors,
} from '@/domain/job-application/hooks/use-apply-form-submit-errors';
import { LIFormFieldValues, LOFormFieldValues } from '@/domain/job-application/types';
import {
  JobApplicationCvDataInput,
  JobApplicationFormFieldInput,
  JobApplicationFormFieldLabel,
  JobApplicationMutationInput,
  JobInteractionPlatform,
  VisibleJob,
  useJobXingApplicationDataLazyQuery,
  useSubmitXingApplicationMutation,
} from '@/graphql/generated';
import { useLoginStates } from '@/hooks';

import { getParsedCvData } from '../../helpers/get-parsed-cv-data';
import { getParsedCvFormData } from '../../helpers/get-parsed-cv-form-data';

const BAD_REQUEST_ERROR_MSG = 'Bad Request';

type UseApplyFormSubmitValue = {
  isJobDeactivated: boolean;
  isSubmitInProgress: boolean;
  submitFormData: (formData: LIFormFieldValues | LOFormFieldValues) => void;
};

export const useApplyFormSubmit = ({
  jobGlobalId,
  jobId,
  jobSlug,
  onSubmitSuccess,
  onBadRequestError,
}: {
  jobGlobalId: string;
  jobId: string;
  jobSlug: string;
  onSubmitSuccess: () => void;
  onBadRequestError: () => void;
}): UseApplyFormSubmitValue => {
  const { isLoggedIn } = useLoginStates();
  const [isJobDeactivated, setIsJobDeactivated] = useState<boolean>(false);
  const [currentFormData, setCurrentFormData] = useState<
    ({ label: JobApplicationFormFieldLabel; value: string[] } | undefined)[] | null
  >(null);
  const [currentCvData, setCurrentCvData] = useState<JobApplicationCvDataInput | null>(null);
  const { showSubmitError } = useApplyFormSubmitErrors(jobGlobalId);
  const [submitXingApplicationMutation] = useSubmitXingApplicationMutation();

  const onLISubmitMutationUpdate = useCallback(
    (cache): void => {
      cache.modify({
        id: cache.identify({
          __typename: 'VisibleJob',
          id: jobId,
        }),
        fields: {
          userInteractions(existing) {
            return {
              ...existing,
              application: {
                __typename: 'JobUserXingApplication',
                status: 'PENDING',
                createdAt: new Date().toISOString(),
              },
            };
          },
        },
      });
    },
    [jobId]
  );

  const showErrorAndResetCurrentFormData = useCallback(
    (error: ApplyFormSubmitError) => {
      showSubmitError(error);
      setCurrentFormData(null);
      setCurrentCvData(null);
    },
    [showSubmitError]
  );

  const [getJobXingApplicationData] = useJobXingApplicationDataLazyQuery({
    fetchPolicy: 'no-cache',
    onCompleted: (queryData) => {
      if (queryData?.job?.__typename !== 'VisibleJob') {
        // check job is deactivated
        showErrorAndResetCurrentFormData('deactivatedJob');
        setIsJobDeactivated(true);
      } else if ((queryData?.job as VisibleJob)?.application?.__typename !== 'JobXingApplication') {
        // check job apply type is not valid
        showErrorAndResetCurrentFormData('noXingApply');
      } else {
        // job application is active and has correct application type: proceed to submit the form data

        const inputData: JobApplicationMutationInput = {
          jobId,
          platform: JobInteractionPlatform.Web,
          formData: currentFormData as JobApplicationFormFieldInput[],
        };

        if (currentCvData !== null) {
          inputData.cvData = currentCvData;
        }

        submitXingApplicationMutation({
          variables: {
            input: inputData,
          },
          onCompleted: (mutationData) => {
            if (mutationData?.jobSubmitJobApplication?.error) {
              const isBadRequestError = mutationData.jobSubmitJobApplication.error?.message === BAD_REQUEST_ERROR_MSG;

              showErrorAndResetCurrentFormData(isBadRequestError ? 'badRequest' : 'generic');
              isBadRequestError && onBadRequestError();
            } else {
              onSubmitSuccess();
            }
          },
          onError: () => {
            showErrorAndResetCurrentFormData('generic');
          },
          update: isLoggedIn ? onLISubmitMutationUpdate : undefined,
        });
      }
    },
    onError: () => {
      showErrorAndResetCurrentFormData('generic');
    },
  });

  const submitFormData = useCallback(
    (formData: LIFormFieldValues & LOFormFieldValues) => {
      const parsedFormData = getParsedFormData(formData);
      setCurrentFormData(parsedFormData);

      if (formData.enterCvMode === 'manual') {
        setCurrentFormData(getParsedCvFormData(parsedFormData));
        setCurrentCvData(getParsedCvData(formData.cvData));
      }

      setIsJobDeactivated(false);
      // check if current job posting is still active before data submission
      getJobXingApplicationData({ variables: { slug: jobSlug } });
    },
    [getJobXingApplicationData, jobSlug]
  );

  return {
    isJobDeactivated,
    isSubmitInProgress: !!currentFormData,
    submitFormData,
  };
};
